import {
  draggable,
  dropTargetForElements,
  monitorForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import {
  faArrowRight,
  faGear,
  faQuestionCircle
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import {
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { StandardModal } from '~/components/Global/Design/Modal/StandardModal'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { useUpdateConfig } from '~/domains/configuration/mutations/updateConfig'
import type { DashboardBlock } from '../../types'
import {
  blockItem,
  blockItemDraggedOver,
  blockItemDragging,
  blockItemTitle,
  blocksList,
  dropZone,
  dropZoneActive,
  hiddenBlock,
  hiddenDropZone,
  separator,
  separatorLabel,
  visibleDropZone
} from './DashboardConfiguration.css'

type DashboardConfigurationProps = {
  isOpen: boolean
  onClose: () => void
  blocks: DashboardBlock[]
  onRestartTour?: () => void
}

export const DashboardConfiguration = ({
  isOpen,
  onClose,
  blocks,
  onRestartTour
}: DashboardConfigurationProps): ReactElement | null => {
  const updateConfiguration = useUpdateConfig('dashboard.blocks')
  const [localBlocks, setLocalBlocks] = useState<DashboardBlock[]>(blocks)
  const [isDragOverVisibleZone, setIsDragOverVisibleZone] = useState(false)
  const [isDragOverHiddenZone, setIsDragOverHiddenZone] = useState(false)
  const [draggedBlockId, setDraggedBlockId] = useState<string | null>(null)
  const [draggedOverBlockId, setDraggedOverBlockId] = useState<string | null>(
    null
  )
  const visibleDropZoneRef = useRef<HTMLDivElement | null>(null)
  const hiddenDropZoneRef = useRef<HTMLDivElement | null>(null)
  const blockRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const localBlocksRef = useRef<DashboardBlock[]>(blocks)

  useEffect(() => {
    setLocalBlocks([...blocks])
    localBlocksRef.current = [...blocks]
  }, [blocks])

  // Monitor for drag end events to clear draggedBlockId if drag is cancelled
  useEffect(() => {
    const monitor = monitorForElements({
      onDrop: ({ source }) => {
        // Only clear if this was our block being dragged
        if (source.data.type === 'block') {
          setTimeout(() => {
            setDraggedBlockId(null)
            setDraggedOverBlockId(null)
          }, 100)
        }
      }
    })

    return () => monitor()
  }, [])

  const handleDrop = useCallback(
    (
      draggedBlockId: string,
      targetIndex: number,
      toggleVisibility = false,
      setVisible?: boolean
    ) => {
      setLocalBlocks((prev) => {
        const newBlocks = [...prev]
        const draggedIndex = newBlocks.findIndex(
          (block) => block.id === draggedBlockId
        )

        if (draggedIndex === -1) return prev

        const [draggedBlock] = newBlocks.splice(draggedIndex, 1)
        if (!draggedBlock) return prev

        let updatedBlock = draggedBlock
        if (toggleVisibility) {
          updatedBlock = {
            ...draggedBlock,
            isVisible: !draggedBlock.isVisible
          }
        } else if (setVisible !== undefined) {
          updatedBlock = {
            ...draggedBlock,
            isVisible: setVisible
          }
        }

        newBlocks.splice(targetIndex, 0, updatedBlock)

        const updatedBlocks = newBlocks.map((block, index) => ({
          ...block,
          position: index + 1
        }))

        updateConfiguration.mutate({
          value: updatedBlocks.map(({ id, position, isVisible }) => ({
            id,
            position,
            isVisible
          }))
        })

        // Update ref to keep it in sync
        localBlocksRef.current = updatedBlocks

        return updatedBlocks
      })
    },
    [updateConfiguration]
  )

  useEffect(() => {
    const visibleDropZoneElement = visibleDropZoneRef.current
    const hiddenDropZoneElement = hiddenDropZoneRef.current

    const dropTargets: Array<() => void> = []

    // Register drop target for visible drop zone
    if (visibleDropZoneElement) {
      const visibleZoneDropTarget = dropTargetForElements({
        element: visibleDropZoneElement,
        getData: () => ({ type: 'visible-zone' }),
        onDragEnter: () => setIsDragOverVisibleZone(true),
        onDragLeave: () => setIsDragOverVisibleZone(false),
        onDrop: ({ source }) => {
          setIsDragOverVisibleZone(false)
          const blockId = source.data.blockId as string
          const currentVisibleBlocks = localBlocksRef.current.filter(
            (block) => block.isVisible
          )
          handleDrop(blockId, currentVisibleBlocks.length, false, true)
        }
      })
      dropTargets.push(visibleZoneDropTarget)
    }

    // Register drop target for hidden drop zone
    if (hiddenDropZoneElement) {
      const hiddenZoneDropTarget = dropTargetForElements({
        element: hiddenDropZoneElement,
        getData: () => ({ type: 'hidden-zone' }),
        onDragEnter: () => setIsDragOverHiddenZone(true),
        onDragLeave: () => setIsDragOverHiddenZone(false),
        onDrop: ({ source }) => {
          setIsDragOverHiddenZone(false)
          const blockId = source.data.blockId as string
          handleDrop(blockId, localBlocksRef.current.length, false, false)
        }
      })
      dropTargets.push(hiddenZoneDropTarget)
    }

    return () => {
      for (const cleanup of dropTargets) {
        cleanup()
      }
    }
  }, [handleDrop])

  useEffect(() => {
    const cleanupFunctions: Array<() => void> = []

    localBlocks.forEach((block, index) => {
      const element = blockRefs.current.get(block.id || '')
      if (!element || !block.id) return

      const dragElement = draggable({
        element,
        getInitialData: () => ({ blockId: block.id, type: 'block' }),
        // Generate a custom native drag preview for the block (webkit has issue to generate the correct preview)
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: () => ({ x: 16, y: 16 }),
            render: ({ container }) => {
              const preview = element.cloneNode(true) as HTMLElement
              const computedStyle = window.getComputedStyle(element)
              preview.style.width = `${element.offsetWidth}px`
              preview.style.padding = computedStyle.padding
              preview.style.backgroundColor = computedStyle.backgroundColor
              preview.style.border = computedStyle.border
              preview.style.borderRadius = computedStyle.borderRadius
              preview.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
              const titleElement = element.querySelector('span')
              const titlePreview = preview.querySelector('span')
              if (titleElement && titlePreview) {
                const titleStyle = window.getComputedStyle(titleElement)
                titlePreview.style.fontSize = titleStyle.fontSize
                titlePreview.style.fontWeight = titleStyle.fontWeight
                titlePreview.style.color = titleStyle.color
              }
              container.appendChild(preview)
              document.body.appendChild(container)
            }
          })
        },
        onDragStart: () => setDraggedBlockId(block.id || null),
        onDrop: () => {
          setDraggedBlockId(null)
          setDraggedOverBlockId(null)
        }
      })

      const dropElement = dropTargetForElements({
        element,
        getData: () => ({ targetIndex: index, type: 'block' }),
        onDragEnter: () => setDraggedOverBlockId(block.id || null),
        onDragLeave: () => setDraggedOverBlockId(null),
        onDrop: ({ source }) => {
          setDraggedOverBlockId(null)
          const draggedBlockId = source.data.blockId as string
          if (draggedBlockId !== block.id) {
            // Find the dragged block to check its visibility
            const draggedBlock = localBlocksRef.current.find(
              (b) => b.id === draggedBlockId
            )
            const targetBlock = block

            if (draggedBlock && targetBlock) {
              // If dragging from different visibility state, change visibility
              if (draggedBlock.isVisible !== targetBlock.isVisible) {
                handleDrop(draggedBlockId, index, false, targetBlock.isVisible)
              } else {
                // Same visibility, just reorder
                handleDrop(draggedBlockId, index, false)
              }
            }
          }
        }
      })

      cleanupFunctions.push(dragElement, dropElement)
    })

    return () => {
      for (const cleanup of cleanupFunctions) {
        cleanup()
      }
    }
  }, [localBlocks, handleDrop])

  const visibleBlocks = localBlocks.filter((block) => block.isVisible)
  const hiddenBlocks = localBlocks.filter((block) => !block.isVisible)
  const sortedBlocks = [...visibleBlocks, ...hiddenBlocks]

  // Determine which drop zones to show based on dragged block and target side emptiness
  const draggedBlock = draggedBlockId
    ? localBlocks.find((block) => block.id === draggedBlockId)
    : null
  const showVisibleDropZone = draggedBlock
    ? !draggedBlock.isVisible && visibleBlocks.length === 0 // Show if dragging a hidden block AND no visible blocks exist
    : false
  const showHiddenDropZone = draggedBlock
    ? draggedBlock.isVisible && hiddenBlocks.length === 0 // Show if dragging a visible block AND no hidden blocks exist
    : false

  return (
    <StandardModal
      open={isOpen}
      onClose={onClose}
      title="Configuration du tableau de bord"
      icon={faGear}
    >
      <Alert
        variation={AlertVariation.Info}
        message="Glissez les blocs pour les réorganiser. Glissez un bloc au-delà de la ligne de séparation pour changer sa visibilité."
        onDismiss={false}
      />
      <div className={blocksList}>
        {visibleBlocks.length === 0 && localBlocks.length > 0 && (
          <>
            <div
              ref={visibleDropZoneRef}
              className={clsx(
                dropZone,
                visibleDropZone,
                isDragOverVisibleZone && dropZoneActive
              )}
              style={{ display: showVisibleDropZone ? 'flex' : 'none' }}
            >
              {isDragOverVisibleZone
                ? 'Relâcher pour afficher le bloc'
                : 'Glisser ici pour afficher le bloc'}
            </div>

            <div className={separator}>
              <span className={separatorLabel}>Blocs masqués</span>
            </div>

            <div
              ref={hiddenDropZoneRef}
              className={clsx(
                dropZone,
                hiddenDropZone,
                isDragOverHiddenZone && dropZoneActive
              )}
              style={{ display: showHiddenDropZone ? 'flex' : 'none' }}
            >
              {isDragOverHiddenZone
                ? 'Relâcher pour masquer le bloc'
                : 'Glisser ici pour masquer le bloc'}
            </div>
          </>
        )}

        {sortedBlocks.map((block, index) => {
          const isLastVisible = index === visibleBlocks.length - 1
          const isVisible = block.isVisible

          return (
            <div key={block.id}>
              <div
                className={clsx(
                  draggedOverBlockId === block.id && blockItemDraggedOver
                )}
              >
                <div
                  ref={(el) => {
                    if (el && block.id) {
                      blockRefs.current.set(block.id, el)
                    }
                  }}
                  className={clsx(
                    blockItem,
                    !isVisible && hiddenBlock,
                    draggedBlockId === block.id && blockItemDragging
                  )}
                >
                  <span className={blockItemTitle}>{block.title}</span>
                </div>
              </div>

              {isLastVisible && visibleBlocks.length > 0 && (
                <>
                  <div
                    ref={visibleDropZoneRef}
                    className={clsx(
                      dropZone,
                      visibleDropZone,
                      isDragOverVisibleZone && dropZoneActive
                    )}
                    style={{ display: showVisibleDropZone ? 'flex' : 'none' }}
                  >
                    {isDragOverVisibleZone
                      ? 'Relâcher pour afficher le bloc'
                      : 'Glisser ici pour afficher le bloc'}
                  </div>

                  <div className={separator}>
                    <span className={separatorLabel}>Blocs masqués</span>
                  </div>

                  <div
                    ref={hiddenDropZoneRef}
                    className={clsx(
                      dropZone,
                      hiddenDropZone,
                      isDragOverHiddenZone && dropZoneActive
                    )}
                    style={{ display: showHiddenDropZone ? 'flex' : 'none' }}
                  >
                    {isDragOverHiddenZone
                      ? 'Relâcher pour masquer le bloc'
                      : 'Glisser ici pour masquer le bloc'}
                  </div>
                </>
              )}
            </div>
          )
        })}
      </div>
      {onRestartTour && (
        <Alert
          variation={AlertVariation.Primary}
          icon={faQuestionCircle}
          onDismiss={false}
        >
          <ButtonGroup align="spaceBetween">
            Besoin d'aide ? Relancez le tutoriel d'introduction.
            <Button
              onClick={() => {
                onClose()
                setTimeout(() => onRestartTour(), 300)
              }}
              buttonSize="small"
              variation={ButtonVariation.PrimaryInversed}
            >
              <FontAwesomeIcon icon={faArrowRight} />
              Relancer le tutoriel
            </Button>
          </ButtonGroup>
        </Alert>
      )}
    </StandardModal>
  )
}
